import { json, redirect } from "@vercel/remix";
import type { ActionFunctionArgs, LoaderFunctionArgs } from "@vercel/remix";
import {
  Form,
  useActionData,
  useLoaderData,
  useNavigation,
  NavLink,
} from "@remix-run/react";
import { Button, Text, TextField, RadixForm, Flex } from "@figment-fitness/ui";
import { Card, Heading } from "@radix-ui/themes";
import {
  getAuthSession,
  signInWithEmail,
} from "@figment-fitness/database/auth";
import { TitleBodyContainer } from "@figment-fitness/ui/components/title-body-container";
import { figmentRoutes } from "~/utils/figment/routes";
import DefaultErrorBoundary from "@figment-fitness/ui/components/error-boundary";
import { auth } from "~/lib/auth.server";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const session = await getAuthSession(auth, request);
  const headers = new Headers();

  if (session?.user) {
    throw redirect(figmentRoutes.fitnessTenantList(), { headers });
  }

  return json({
    success: false,
    // ローカル開発時のみテストアカウントのクイック入力を表示する
    isDevelopment: process.env.NODE_ENV === "development",
  });
};

// seed で作成される開発用アカウント（admin はフィグメントスタッフのみログイン可）
const TEST_ACCOUNTS = [
  {
    label: "フィグメントスタッフ",
    email: "figment@example.com",
    password: "password",
  },
] as const;

export const action = async ({ request }: ActionFunctionArgs) => {
  const formData = await request.formData();
  const result = await signInWithEmail(auth, request, {
    email: formData.get("email") as string,
    password: formData.get("password") as string,
  });

  if (!result.ok) {
    return json({ success: false, error: "ログインに失敗しました" });
  }

  throw redirect(figmentRoutes.top(), { headers: result.headers });
};

export const ErrorBoundary = DefaultErrorBoundary;

export default function Login() {
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const { isDevelopment } = useLoaderData<typeof loader>();

  const fillTestAccount = (email: string, password: string) => {
    const form = document.getElementById(
      "login-form"
    ) as HTMLFormElement | null;
    if (!form) return;
    const set = (name: string, value: string) => {
      const el = form.elements.namedItem(name) as HTMLInputElement | null;
      if (!el) return;
      el.value = value;
      el.dispatchEvent(new Event("input", { bubbles: true }));
    };
    set("email", email);
    set("password", password);
  };

  return (
    <TitleBodyContainer title="フィグメント管理画面ログイン" level={1}>
      <Flex direction="column" gap="4">
        <RadixForm.Root
          method="post"
          className="space-y-4 w-full max-w-md mx-auto mt-8"
        >
          <Form method="post" id="login-form">
            <Flex direction="column" gap="4">
              <RadixForm.Field name="email">
                <RadixForm.Label>メールアドレス</RadixForm.Label>
                <RadixForm.Control asChild>
                  <TextField.Root type="email" radius="large" required />
                </RadixForm.Control>
                <RadixForm.Message match="valueMissing">
                  メールアドレスを入力してください
                </RadixForm.Message>
                <RadixForm.Message match="typeMismatch">
                  有効なメールアドレスを入力してください
                </RadixForm.Message>
              </RadixForm.Field>

              <RadixForm.Field name="password">
                <RadixForm.Label>パスワード</RadixForm.Label>
                <RadixForm.Control asChild>
                  <TextField.Root type="password" radius="large" required />
                </RadixForm.Control>
                <RadixForm.Message match="valueMissing">
                  パスワードを入力してください
                </RadixForm.Message>
              </RadixForm.Field>

              {actionData?.error && (
                <Text color="red" size="2">
                  {actionData.error}
                </Text>
              )}

              <RadixForm.Submit asChild>
                <Button disabled={navigation.state === "submitting"}>
                  {navigation.state === "submitting"
                    ? "ログイン中..."
                    : "ログイン"}
                </Button>
              </RadixForm.Submit>
            </Flex>
          </Form>
        </RadixForm.Root>

        <Text color="blue">
          <NavLink to={figmentRoutes.authSendPasswordResetEmail()}>
            パスワードをお忘れですか?
          </NavLink>
        </Text>

        {isDevelopment && (
          <Card
            className="w-full max-w-md mx-auto"
            style={{ marginTop: "8px" }}
          >
            <Flex direction="column" gap="2">
              <Heading size="2" color="gray">
                開発用テストアカウント（クリックで入力）
              </Heading>
              {TEST_ACCOUNTS.map((account) => (
                <Button
                  key={account.email}
                  type="button"
                  variant="soft"
                  color="gray"
                  size="2"
                  onClick={() =>
                    fillTestAccount(account.email, account.password)
                  }
                >
                  <Flex direction="column" align="start" gap="0">
                    <Text size="2">{account.label}</Text>
                    <Text size="1" color="gray">
                      {account.email} / {account.password}
                    </Text>
                  </Flex>
                </Button>
              ))}
            </Flex>
          </Card>
        )}
      </Flex>
    </TitleBodyContainer>
  );
}
